import { Grid } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import "./DropDown.scss";
import { DropDownSVG } from "../../assets";

type Variant = "EXPANDING" | "FADING";
type IconVariant = "NORMAL" | "BIG";
type Size = "NORMAL" | "SMALL";

const variantToClassMap: Record<Variant, string> = {
  EXPANDING: "expanding",
  FADING: "fading",
};

const iconVariantToClassMap: Record<IconVariant, string> = {
  NORMAL: "drop-down__icon--normal",
  BIG: "drop-down__icon--big",
};

const sizeToClassMap: Record<Size, string> = {
  NORMAL: "drop-down__button--normal",
  SMALL: "drop-down__button--small",
};

type Option = { label: string | JSX.Element; value: string; order?: -1 | 1 };

const DropDown = ({
  value,
  options,
  placeholder,
  variant = "FADING",
  iconVariant = "NORMAL",
  size = "NORMAL",
  className,
  onClick,
  optionsClassName,
}: {
  value: string | null;
  options: Option[];
  placeholder: string;
  variant?: Variant;
  iconVariant?: IconVariant;
  size?: Size;
  className?: string;
  onClick: (value: string, order?: -1 | 1) => void;
  optionsClassName?: string;
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropDown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClick = useCallback(
    (item: Option) => {
      const foundObject = options.find(
        (obj) => obj.value === item.value && obj.order === item.order,
      );

      if (!foundObject) {
        console.error(`DropDown > Couldn't find clicked option, item=${item}`);
        return;
      }

      onClick(foundObject.value);

      setIsOpen(false);
    },
    [onClick, options],
  );

  const renderedOptions = useMemo(() => {
    return options.map((item, index) => (
      <Grid key={`${item.value}_${index}`} onClick={() => handleClick(item)}>
        {item.label}
      </Grid>
    ));
  }, [handleClick, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Grid
      className={`drop-down ${isOpen ? "drop-down__open" : ""} ${className} z-10`}
      ref={dropdownRef}
    >
      <Grid
        className={`drop-down__button ${sizeToClassMap[size]} loop-text ${
          isOpen ? "drop-down__button-open" : ""
        }`}
        onClick={toggleDropDown}
      >
        {value
          ? options.map((option) => (
              <Grid
                key={option.value}
                className={`drop-down__chosen-option-text ${
                  option.value === value
                    ? "drop-down__chosen-option-text-show"
                    : ""
                }`}
              >
                {option.label}
              </Grid>
            ))
          : placeholder}

        <Grid
          className={`drop-down__icon ${isOpen ? "drop-down__icon-open" : ""}`}
        >
          <DropDownSVG className={iconVariantToClassMap[iconVariant]} />
        </Grid>
      </Grid>

      {variantToClassMap[variant] === "expanding" ? (
        <Grid
          className={`drop-down__options ${optionsClassName} ${variantToClassMap[variant]}`}
        >
          <Grid className="drop-down__options-container">
            {renderedOptions}
          </Grid>
        </Grid>
      ) : (
        <Grid
          className={`drop-down__options ${optionsClassName} ${variantToClassMap[variant]}`}
        >
          {renderedOptions}
        </Grid>
      )}
    </Grid>
  );
};

export default memo(DropDown);
